// ConfigPage.tsx (fully updated)
import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ClientsConfig } from './ClientsConfig';
import { TasksConfig } from './TasksConfig';
import { UsersConfig } from './UsersConfig';
import { PriorityConfig } from './PriorityConfig';
import { StatusConfig } from './StatusConfig';
import { PreferencesConfig } from './PreferencesConfig';
import { SubTasksConfig } from './SubTasksConfig';
import { Button } from '@/components/common/Button';
import { Download, Plus, Upload } from 'lucide-react';
import { InvoiceConfig } from './InvoiceConfig';
import CreateStatusModal from '@/components/modals/CreateStatusModal';
import { AddClientModal } from '@/components/modals/AddClientModal';
import { AddTaskModal } from '@/components/modals/AddTaskModal';
import { AddSubTaskModal } from '@/components/modals/AddSubTaskModal';
import CreatePriorityModal from '@/components/modals/CreatePriorityModal';
import { AddUserModal } from '@/components/modals/AddUserModal';

// Import other modals as needed
// import CreateClientModal from '@/components/modals/CreateClientModal';
// import CreateTaskModal from '@/components/modals/CreateTaskModal';
// import CreateUserModal from '@/components/modals/CreateUserModal';
// import CreatePriorityModal from '@/components/modals/CreatePriorityModal';
// import CreateSubTaskModal from '@/components/modals/CreateSubTaskModal';
import { setActiveTabFunction, setOpenModalFunction } from '@/components/keyboard/shortcutsConfig';

import { setConfigPageFunctions } from '@/components/keyboard/shortcutsConfig';

export const ConfigPage: React.FC = () => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();

  // Modal states for each tab
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const [isSubTaskModalOpen, setIsSubTaskModalOpen] = useState(false);


  const tabs = [
    { id: 'clients', label: 'Clients' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'sub-tasks', label: 'Sub-tasks' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'priority', label: 'Priority' },
    { id: 'status', label: 'Status' },
    { id: 'users', label: 'Users' },
    { id: 'preferences', label: 'Preferences' },
  ];


 useEffect(() => {
    setConfigPageFunctions({
      setActiveTab: (tabId: string) => {
        console.log('⌨️ Switching to tab:', tabId);
        navigate(`/configurations/${tabId}`);
      },
    });

    return () => {
      setConfigPageFunctions({});
    };
  }, [navigate]);

  // Handler functions for export/import buttons
  const handleExportData = () => {
    console.log(`Exporting data for ${currentPath}`);
    // Add export logic here
  };

  const handleImportCSV = () => {
    console.log(`Importing CSV for ${currentPath}`);
    // Add import logic here
  };

  const handleResetToDefault = () => {
    console.log(`Resetting ${currentPath} to default`);
    // Add reset logic here
  };

  const handleSaveChanges = () => {
    console.log(`Saving changes for ${currentPath}`);
    // Add save logic here
  };

  // Dynamic button configurations for each tab
  const getActionButtons = () => {
    switch(currentPath) {
      case 'clients':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Download size={16} />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Upload size={16} />}
              onClick={handleImportCSV}
            >
              Import CSV
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setIsClientModalOpen(true)}
            >
              Add Client
            </Button>
          </>
        );
      case 'tasks':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Download size={16} />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Upload size={16} />}
              onClick={handleImportCSV}
            >
              Import CSV
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setIsTaskModalOpen(true)}
            >
              Add Task
            </Button>
          </>
        );
      case 'sub-tasks':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Download size={16} />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Upload size={16} />}
              onClick={handleImportCSV}
            >
              Import CSV
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setIsSubTaskModalOpen(true)}
            >
              Add Sub-task
            </Button>
          </>
        );
      case 'invoices':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleResetToDefault}
            >
              Reset to Default
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleSaveChanges}
            >
              Save Changes
            </Button>
          </>
        );
      case 'priority':
        return (
          <Button 
            variant="primary" 
            size="sm"
            icon={<Plus size={16} />}
            onClick={() => setIsPriorityModalOpen(true)}
          >
            Add Priority
          </Button>
        );
      case 'status':
        return (
          <Button 
            variant="primary" 
            size="sm"
            icon={<Plus size={16} />}
            onClick={() => setIsStatusModalOpen(true)}
          >
            Add Status
          </Button>
        );
      case 'users':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Download size={16} />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setIsUserModalOpen(true)}
            >
              Add User
            </Button>
          </>
        );
      case 'preferences':
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleResetToDefault}
            >
              Reset Preferences
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleSaveChanges}
            >
              Save Preferences
            </Button>
          </>
        );
      default:
        return (
          <>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Download size={16} />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
            <Button 
              variant="secondary" 
              size="sm"
              icon={<Upload size={16} />}
              onClick={handleImportCSV}
            >
              Import CSV
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setIsClientModalOpen(true)}
            >
              Add Client
            </Button>
          </>
        );
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Tab Navigation and Action Buttons */}
      <div className="bg-white border-b border-gray-300">
        <div className="px-6 flex items-center justify-between">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => navigate(`/configurations/${tab.id}`)}
                className={`
                  px-4 py-3 text-sm font-medium border-b-2 transition-colors
                  ${currentPath === tab.id
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-600 border-transparent hover:text-gray-800'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            {getActionButtons()}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 bg-gray-50">
        <Routes>
          <Route path="clients" element={<ClientsConfig />} />
          <Route path="tasks" element={<TasksConfig />} />
          <Route path="sub-tasks" element={<SubTasksConfig />} />
          <Route path="invoices" element={<InvoiceConfig />} />
          <Route path="priority" element={<PriorityConfig />} />
          <Route path="status" element={<StatusConfig />} />
          <Route path="users" element={<UsersConfig />} />
          <Route path="preferences" element={<PreferencesConfig />} />
          <Route path="*" element={<ClientsConfig />} />
        </Routes>
      </div>

      {/* Modals */}
      <CreateStatusModal 
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        onStatusCreated={(newStatus) => {
          console.log('New status created:', newStatus);
          setIsStatusModalOpen(false);
        }}
      />  
      <AddClientModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}      
      />
      <AddTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}      
      />
      
      <AddSubTaskModal
        isOpen={isSubTaskModalOpen}
        onClose={() => setIsSubTaskModalOpen(false)}      
      />

<CreatePriorityModal
  isOpen={isPriorityModalOpen}
  onClose={() => setIsPriorityModalOpen(false)}
  // onPriorityCreated={handlePriorityCreated}
/>

      {/* <CreateTaskModal 
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        onTaskCreated={(newTask) => {
          console.log('New task created:', newTask);
          setIsTaskModalOpen(false);
        }}
      /> */}

<AddUserModal
 isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
/>
      {/* <CreateUserModal 
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        onUserCreated={(newUser) => {
          console.log('New user created:', newUser);
          setIsUserModalOpen(false);
        }}
      /> */}

      {/* <CreatePriorityModal 
        isOpen={isPriorityModalOpen}
        onClose={() => setIsPriorityModalOpen(false)}
        onPriorityCreated={(newPriority) => {
          console.log('New priority created:', newPriority);
          setIsPriorityModalOpen(false);
        }}
      /> */}

      {/* <CreateSubTaskModal 
        isOpen={isSubTaskModalOpen}
        onClose={() => setIsSubTaskModalOpen(false)}
        onSubTaskCreated={(newSubTask) => {
          console.log('New sub-task created:', newSubTask);
          setIsSubTaskModalOpen(false);
        }}
      /> */}
     
    </div>    
  );
};